import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Plus, Trash2, Edit3, ChevronDown, ChevronUp, Save, X, Eye, Check,
  XCircle, Layers, ListChecks, Users, Loader2, MessageSquare, GripVertical,
  Sparkles, Image as ImageIcon
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

const TABS = [
  { id: "levels", label: "Levels & Tasks", icon: Layers },
  { id: "submissions", label: "Submissions", icon: ListChecks },
  { id: "users", label: "Users", icon: Users },
];

export default function AdminAIFounder() {
  const [tab, setTab] = useState("levels");

  return (
    <div data-testid="admin-ai-founder">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-outfit font-bold text-gray-900">AI Founder Program</h1>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-dm-sans font-medium rounded-md transition-colors ${
              tab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "levels" && <LevelsTab />}
      {tab === "submissions" && <SubmissionsTab />}
      {tab === "users" && <UsersTab />}
    </div>
  );
}

// ─── Levels & Tasks Tab ───
function LevelsTab() {
  const [levels, setLevels] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLevelForm, setShowLevelForm] = useState(false);
  const [editLevel, setEditLevel] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(null); // level_id
  const [editTask, setEditTask] = useState(null);
  const [expandedLevel, setExpandedLevel] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [levelsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/ai-founder/admin/levels`, getAuth()),
        axios.get(`${API}/ai-founder/admin/tasks`, getAuth()),
      ]);
      setLevels(levelsRes.data);
      setTasks(tasksRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const deleteLevel = async (levelId) => {
    if (!window.confirm("Delete this level and all its tasks?")) return;
    await axios.delete(`${API}/ai-founder/admin/levels/${levelId}`, getAuth());
    fetchData();
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    await axios.delete(`${API}/ai-founder/admin/tasks/${taskId}`, getAuth());
    fetchData();
  };

  if (loading) return <div className="flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-4">
      <button
        data-testid="add-level-btn"
        onClick={() => { setEditLevel(null); setShowLevelForm(true); }}
        className="flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-dm-sans font-bold hover:bg-gray-800 transition-colors"
      >
        <Plus size={16} /> Add Level
      </button>

      {levels.length === 0 && (
        <div className="text-center py-12 text-gray-400 font-dm-sans">
          No levels created yet. Add your first level to get started.
        </div>
      )}

      {levels.map(level => {
        const levelTasks = tasks.filter(t => t.level_id === level.level_id);
        const isExpanded = expandedLevel === level.level_id;

        return (
          <div key={level.level_id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="w-8 h-8 bg-black text-white rounded flex items-center justify-center text-sm font-bold font-outfit">
                {level.order}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-outfit font-bold text-gray-900 text-sm">{level.title}</h3>
                <p className="text-xs text-gray-500 font-dm-sans truncate">{level.description}</p>
              </div>
              <span className="text-xs text-gray-400 font-dm-sans">{levelTasks.length} tasks</span>
              <button onClick={() => { setEditLevel(level); setShowLevelForm(true); }} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid={`edit-level-${level.level_id}`}>
                <Edit3 size={14} />
              </button>
              <button onClick={() => deleteLevel(level.level_id)} className="p-1.5 text-gray-400 hover:text-red-500" data-testid={`delete-level-${level.level_id}`}>
                <Trash2 size={14} />
              </button>
              <button onClick={() => setExpandedLevel(isExpanded ? null : level.level_id)} className="p-1.5 text-gray-400 hover:text-gray-700" data-testid={`toggle-level-${level.level_id}`}>
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-3">
                {levelTasks.map(task => (
                  <div key={task.task_id} className="flex items-start gap-3 bg-white border border-gray-200 rounded-md px-4 py-3">
                    <GripVertical size={14} className="text-gray-300 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-dm-sans font-medium text-gray-900">{task.title}</p>
                      <p className="text-xs text-gray-500 font-dm-sans mt-0.5">{task.description}</p>
                      <span className="inline-block mt-1 text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-dm-sans">
                        Evidence: {task.evidence_type}
                      </span>
                    </div>
                    <button onClick={() => { setEditTask(task); setShowTaskForm(level.level_id); }} className="p-1 text-gray-400 hover:text-gray-700" data-testid={`edit-task-${task.task_id}`}>
                      <Edit3 size={13} />
                    </button>
                    <button onClick={() => deleteTask(task.task_id)} className="p-1 text-gray-400 hover:text-red-500" data-testid={`delete-task-${task.task_id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}

                <button
                  data-testid={`add-task-${level.level_id}`}
                  onClick={() => { setEditTask(null); setShowTaskForm(level.level_id); }}
                  className="flex items-center gap-1.5 text-xs font-dm-sans font-semibold text-gray-500 hover:text-black transition-colors"
                >
                  <Plus size={13} /> Add Task
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Level Form Dialog */}
      <LevelFormDialog
        open={showLevelForm}
        onClose={() => { setShowLevelForm(false); setEditLevel(null); }}
        level={editLevel}
        nextOrder={levels.length + 1}
        onSaved={fetchData}
      />

      {/* Task Form Dialog */}
      <TaskFormDialog
        open={!!showTaskForm}
        onClose={() => { setShowTaskForm(null); setEditTask(null); }}
        levelId={showTaskForm}
        task={editTask}
        nextOrder={(tasks.filter(t => t.level_id === showTaskForm).length) + 1}
        onSaved={fetchData}
      />
    </div>
  );
}

function LevelFormDialog({ open, onClose, level, nextOrder, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", order: nextOrder });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (level) {
      setForm({ title: level.title, description: level.description || "", order: level.order });
    } else {
      setForm({ title: "", description: "", order: nextOrder });
    }
  }, [level, nextOrder]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (level) {
        await axios.put(`${API}/ai-founder/admin/levels/${level.level_id}`, form, getAuth());
      } else {
        await axios.post(`${API}/ai-founder/admin/levels`, form, getAuth());
      }
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-outfit text-gray-900">{level ? "Edit Level" : "Add Level"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Title *</label>
            <input
              data-testid="level-title-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
              placeholder="e.g. Level 1: AI Foundations"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Description</label>
            <textarea
              data-testid="level-desc-input"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm h-20 resize-none"
              placeholder="Describe this level..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Order</label>
            <input
              data-testid="level-order-input"
              type="number"
              min="1"
              value={form.order}
              onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 1 }))}
              className="w-24 border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
            />
          </div>
          <button
            data-testid="save-level-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60"
          >
            {saving ? "Saving..." : (level ? "Update Level" : "Create Level")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TaskFormDialog({ open, onClose, levelId, task, nextOrder, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", evidence_type: "file", order: nextOrder });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setForm({ title: task.title, description: task.description || "", evidence_type: task.evidence_type || "file", order: task.order });
    } else {
      setForm({ title: "", description: "", evidence_type: "file", order: nextOrder });
    }
  }, [task, nextOrder]);

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (task) {
        await axios.put(`${API}/ai-founder/admin/tasks/${task.task_id}`, { ...form, level_id: levelId }, getAuth());
      } else {
        await axios.post(`${API}/ai-founder/admin/tasks`, { ...form, level_id: levelId }, getAuth());
      }
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="font-outfit text-gray-900">{task ? "Edit Task" : "Add Task"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Task Title *</label>
            <input
              data-testid="task-title-input"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
              placeholder="e.g. Complete AI Quiz"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Description</label>
            <textarea
              data-testid="task-desc-input"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm h-20 resize-none"
              placeholder="Describe what the user needs to do..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Evidence Type</label>
            <select
              data-testid="task-evidence-type"
              value={form.evidence_type}
              onChange={e => setForm(f => ({ ...f, evidence_type: e.target.value }))}
              className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm bg-white"
            >
              <option value="file">File Upload</option>
              <option value="url">URL / Link</option>
              <option value="text">Text Response</option>
              <option value="any">Any (File, URL, or Text)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Order</label>
            <input
              type="number" min="1" value={form.order}
              onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 1 }))}
              className="w-24 border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm"
            />
          </div>
          <button
            data-testid="save-task-btn"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-black text-white font-outfit font-bold py-2.5 hover:bg-gray-800 transition-colors disabled:opacity-60"
          >
            {saving ? "Saving..." : (task ? "Update Task" : "Create Task")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Submissions Tab ───
function SubmissionsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [reviewModal, setReviewModal] = useState(null);

  const fetchSubs = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/ai-founder/admin/submissions${params}`, getAuth());
      setSubmissions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubs(); }, [filter]);

  const STATUS_FILTERS = [
    { id: "all", label: "All" },
    { id: "submitted", label: "Pending" },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
  ];

  if (loading) return <div className="flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading submissions...</div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {STATUS_FILTERS.map(f => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 text-xs font-dm-sans font-semibold rounded-full transition-colors ${
              filter === f.id ? "bg-black text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {submissions.length === 0 && (
        <div className="text-center py-12 text-gray-400 font-dm-sans">No submissions found.</div>
      )}

      <div className="space-y-3">
        {submissions.map(sub => (
          <div key={sub.submission_id} className="bg-white border border-gray-200 rounded-lg px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {sub.user?.picture ? (
                  <img src={sub.user.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                    {(sub.user?.name || "?")[0]}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-dm-sans font-medium text-gray-900">{sub.user?.name || "Unknown"}</span>
                  <span className="text-xs text-gray-400">{sub.user?.email}</span>
                </div>
                <p className="text-xs text-gray-500 font-dm-sans mt-0.5">
                  <span className="font-medium text-gray-700">{sub.level_title}</span> &rsaquo; {sub.task_title}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <StatusBadge status={sub.status} />
                  <span className="text-[11px] text-gray-400 font-dm-sans">{sub.submitted_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                {/* Evidence preview */}
                <div className="mt-2 flex gap-2 flex-wrap">
                  {sub.evidence?.file_name && (
                    <a
                      href={`${API}/ai-founder/evidence/${sub.submission_id}/file`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs bg-gray-50 border border-gray-200 px-2.5 py-1 rounded text-blue-600 hover:bg-blue-50 font-dm-sans"
                      data-testid={`view-evidence-${sub.submission_id}`}
                    >
                      {sub.evidence.file_name}
                    </a>
                  )}
                  {sub.evidence?.url && (
                    <a href={sub.evidence.url} target="_blank" rel="noopener noreferrer"
                      className="text-xs bg-gray-50 border border-gray-200 px-2.5 py-1 rounded text-blue-600 hover:bg-blue-50 font-dm-sans">
                      View Link
                    </a>
                  )}
                  {sub.evidence?.text && (
                    <span className="text-xs text-gray-500 font-dm-sans italic truncate max-w-[200px]">"{sub.evidence.text}"</span>
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                {sub.status === "submitted" && (
                  <>
                    <button
                      data-testid={`approve-${sub.submission_id}`}
                      onClick={() => setReviewModal({ sub, action: "approve" })}
                      className="p-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-md transition-colors"
                      title="Approve"
                    >
                      <Check size={15} />
                    </button>
                    <button
                      data-testid={`reject-${sub.submission_id}`}
                      onClick={() => setReviewModal({ sub, action: "reject" })}
                      className="p-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                      title="Reject"
                    >
                      <XCircle size={15} />
                    </button>
                  </>
                )}
                {sub.status !== "submitted" && (
                  <button
                    onClick={() => setReviewModal({ sub, action: null })}
                    className="p-2 bg-gray-50 text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                    title="View details"
                  >
                    <Eye size={15} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {reviewModal && (
        <ReviewDialog
          submission={reviewModal.sub}
          defaultAction={reviewModal.action}
          onClose={() => setReviewModal(null)}
          onSaved={fetchSubs}
        />
      )}
    </div>
  );
}

function ReviewDialog({ submission, defaultAction, onClose, onSaved }) {
  const [action, setAction] = useState(defaultAction || "approve");
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiReview, setAiReview] = useState(submission.ai_review || "");
  const [aiLoading, setAiLoading] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotPath, setScreenshotPath] = useState("");
  const [screenshotFilename, setScreenshotFilename] = useState("");
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);
  const screenshotRef = React.useRef(null);

  const handleAiReview = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai-founder/admin/submissions/${submission.submission_id}/ai-review`, {}, getAuth());
      setAiReview(res.data.ai_review);
    } catch (err) {
      alert(err.response?.data?.detail || "AI review failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleScreenshotSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));

    // Upload immediately
    setUploadingScreenshot(true);
    try {
      const formData = new FormData();
      formData.append("screenshot", file);
      const res = await axios.post(
        `${API}/ai-founder/admin/submissions/${submission.submission_id}/upload-screenshot`,
        formData,
        { ...getAuth(), headers: { ...getAuth().headers, "Content-Type": "multipart/form-data" } }
      );
      setScreenshotPath(res.data.screenshot_path);
      setScreenshotFilename(res.data.filename);
    } catch (err) {
      alert("Screenshot upload failed");
      setScreenshot(null);
      setScreenshotPreview(null);
    } finally {
      setUploadingScreenshot(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ai-founder/admin/submissions/${submission.submission_id}/review`, {
        action,
        feedback,
        ai_review: aiReview,
        screenshot_path: screenshotPath,
        screenshot_filename: screenshotFilename
      }, getAuth());
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to review");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-outfit text-gray-900">Review Submission</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="bg-gray-50 rounded-lg p-3 text-sm font-dm-sans">
            <p><strong>User:</strong> {submission.user?.name} ({submission.user?.email})</p>
            <p><strong>Level:</strong> {submission.level_title}</p>
            <p><strong>Task:</strong> {submission.task_title}</p>
          </div>

          {/* Evidence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Evidence</label>
            <div className="flex gap-2 flex-wrap">
              {submission.evidence?.file_name && (
                <a
                  href={`${API}/ai-founder/evidence/${submission.submission_id}/file`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-sm bg-blue-50 border border-blue-200 px-3 py-1.5 rounded text-blue-700 hover:bg-blue-100 font-dm-sans"
                >
                  View: {submission.evidence.file_name}
                </a>
              )}
              {submission.evidence?.url && (
                <a href={submission.evidence.url} target="_blank" rel="noopener noreferrer"
                  className="text-sm bg-blue-50 border border-blue-200 px-3 py-1.5 rounded text-blue-700 hover:bg-blue-100 font-dm-sans">
                  Open Link
                </a>
              )}
              {submission.evidence?.text && (
                <div className="w-full bg-gray-50 border border-gray-200 rounded p-3 text-sm font-dm-sans text-gray-700 whitespace-pre-wrap">{submission.evidence.text}</div>
              )}
            </div>
          </div>

          {/* AI Review Section */}
          <div className="border border-purple-200 rounded-lg overflow-hidden">
            <div className="bg-purple-50 px-4 py-2.5 flex items-center justify-between">
              <span className="text-sm font-dm-sans font-semibold text-purple-800 flex items-center gap-1.5">
                <Sparkles size={14} /> AI Review
              </span>
              <button
                data-testid="ai-review-btn"
                onClick={handleAiReview}
                disabled={aiLoading}
                className="flex items-center gap-1.5 bg-purple-600 text-white text-xs font-dm-sans font-bold px-3 py-1.5 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {aiLoading ? <><Loader2 size={12} className="animate-spin" /> Analyzing...</> : <><Sparkles size={12} /> Analyze with AI</>}
              </button>
            </div>
            {aiReview && (
              <div className="px-4 py-3 text-sm font-dm-sans text-gray-700 whitespace-pre-wrap leading-relaxed bg-white border-t border-purple-100 max-h-60 overflow-y-auto"
                data-testid="ai-review-content">
                {aiReview}
              </div>
            )}
            {!aiReview && !aiLoading && (
              <div className="px-4 py-4 text-xs text-gray-400 font-dm-sans text-center">
                Click "Analyze with AI" to get GPT-4o suggestions for this submission
              </div>
            )}
          </div>

          {/* Previous feedback */}
          {submission.review_history?.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Previous Reviews</label>
              {submission.review_history.map((r, i) => (
                <div key={i} className="text-xs font-dm-sans text-gray-500 bg-gray-50 rounded p-2 mb-1">
                  <span className={r.action === "approve" ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                    {r.action === "approve" ? "Approved" : "Rejected"}
                  </span>
                  {r.feedback && <span> — {r.feedback}</span>}
                  {r.ai_review && <span className="ml-1 text-purple-500">(AI reviewed)</span>}
                  <span className="ml-2 text-gray-400">{r.reviewed_at?.slice(0, 16)}</span>
                  {r.screenshot_path && (
                    <div className="mt-1">
                      <img
                        src={`${API}/ai-founder/feedback-image?path=${encodeURIComponent(r.screenshot_path)}&authorization=${encodeURIComponent(localStorage.getItem("admin_token"))}`}
                        alt="Feedback screenshot"
                        className="max-w-full max-h-32 rounded border border-gray-200 mt-1"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {defaultAction && (
            <>
              {/* Action choice */}
              <div className="flex gap-3">
                <button
                  onClick={() => setAction("approve")}
                  className={`flex-1 py-2.5 text-sm font-dm-sans font-bold rounded-md transition-colors ${
                    action === "approve" ? "bg-green-600 text-white" : "bg-gray-100 text-gray-600"
                  }`}
                  data-testid="review-approve-btn"
                >
                  Approve
                </button>
                <button
                  onClick={() => setAction("reject")}
                  className={`flex-1 py-2.5 text-sm font-dm-sans font-bold rounded-md transition-colors ${
                    action === "reject" ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600"
                  }`}
                  data-testid="review-reject-btn"
                >
                  Reject
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">Feedback / Comments</label>
                <textarea
                  data-testid="review-feedback-input"
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  className="w-full border border-gray-300 focus:border-black px-3 py-2.5 text-sm font-dm-sans outline-none rounded-sm h-24 resize-none"
                  placeholder={action === "reject" ? "Explain what needs to be corrected..." : "Optional: Add a note..."}
                />
              </div>

              {/* Screenshot Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-dm-sans">
                  Attach Screenshot <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                {screenshotPreview ? (
                  <div className="relative inline-block">
                    <img src={screenshotPreview} alt="Preview" className="max-w-full max-h-40 rounded border border-gray-200" />
                    <button
                      onClick={() => { setScreenshot(null); setScreenshotPreview(null); setScreenshotPath(""); setScreenshotFilename(""); }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
                      data-testid="remove-screenshot-btn"
                    >
                      <X size={12} />
                    </button>
                    {uploadingScreenshot && (
                      <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded">
                        <Loader2 size={16} className="animate-spin text-gray-600" />
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    data-testid="attach-screenshot-btn"
                    onClick={() => screenshotRef.current?.click()}
                    className="flex items-center gap-1.5 text-sm font-dm-sans text-gray-500 hover:text-gray-800 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg px-4 py-2.5 transition-colors"
                  >
                    <ImageIcon size={14} /> Attach annotated screenshot
                  </button>
                )}
                <input
                  ref={screenshotRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleScreenshotSelect}
                  data-testid="screenshot-input"
                />
              </div>

              <button
                data-testid="submit-review-btn"
                onClick={handleSubmit}
                disabled={saving || uploadingScreenshot}
                className={`w-full font-outfit font-bold py-2.5 transition-colors disabled:opacity-60 ${
                  action === "approve" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                {saving ? "Saving..." : (action === "approve" ? "Approve Submission" : "Reject & Send Feedback")}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Users Tab ───
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/ai-founder/admin/users`, getAuth())
      .then(res => setUsers(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-gray-500"><Loader2 size={16} className="animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-3">
      {users.length === 0 && (
        <div className="text-center py-12 text-gray-400 font-dm-sans">No users have started the program yet.</div>
      )}

      {users.map(u => (
        <div key={u.user_id} className="bg-white border border-gray-200 rounded-lg px-5 py-4 flex items-center gap-4">
          {u.picture ? (
            <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-sm font-bold text-gray-600">
              {(u.name || "?")[0]}
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm font-dm-sans font-medium text-gray-900">{u.name}</p>
            <p className="text-xs text-gray-500 font-dm-sans">{u.email}</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-outfit font-bold text-gray-900">L{u.current_level}</p>
            <p className="text-[10px] text-gray-400 font-dm-sans">Current Level</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-outfit font-bold text-green-600">{u.approved_count}</p>
            <p className="text-[10px] text-gray-400 font-dm-sans">Approved</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-outfit font-bold text-gray-400">{u.total_submissions}</p>
            <p className="text-[10px] text-gray-400 font-dm-sans">Total Subs</p>
          </div>
          {u.completed && (
            <span className="bg-green-100 text-green-700 text-xs font-dm-sans font-bold px-3 py-1 rounded-full">Completed</span>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    submitted: "bg-yellow-50 text-yellow-700 border-yellow-200",
    approved: "bg-green-50 text-green-700 border-green-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    under_review: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <span className={`inline-block px-2.5 py-0.5 text-[11px] font-dm-sans font-semibold border rounded-full ${styles[status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
      {status?.replace("_", " ")}
    </span>
  );
}
